import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLiveVideo } from '../context/LiveVideoContext';
import { useWorkoutStatus } from '../context/WorkoutStatusContext';
import SetLogger from '../components/strength/SetLogger';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { WeeklyGoalCelebrationModal } from '../components/dashboard/WeeklyGoalCelebrationModal';
import { BadgeUnlockToast } from '../components/BadgeUnlockToast';
import { BadgeCelebrationModal } from '../components/BadgeCelebrationModal';
import { BADGE_CATALOG } from '../utils/badges';
import { WorkoutDetailHeader } from '../components/workout-detail/WorkoutDetailHeader';
import { WorkoutVideoPlayer } from '../components/workout-detail/WorkoutVideoPlayer';
import { WorkoutCompleteModal } from '../components/workout-detail/WorkoutCompleteModal';
import { BuddyWorkoutPanel } from '../components/workout-detail/BuddyWorkoutPanel';
import { BuddyPickerDialog } from '../components/workout-detail/BuddyPickerDialog';
import { WorkoutTimer } from '../components/workout-detail/WorkoutTimer';
import { ExerciseList } from '../components/workout-detail/ExerciseList';
import { HeartRateWidget } from '../components/workout-detail/HeartRateWidget';
import { useFloatingTimer } from '../lib/useFloatingTimer';

export const WorkoutDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [workout, setWorkout] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Timer state
  const [isRunning, setIsRunning] = useState(false);
  const [currentExercise, setCurrentExercise] = useState(0);
  const [currentSet, setCurrentSet] = useState(1);
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [isRest, setIsRest] = useState(false);
  const [isSetRest, setIsSetRest] = useState(false);
  const [isTransition, setIsTransition] = useState(false);
  const [workoutComplete, setWorkoutComplete] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  // Weekly goal celebration
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);
  // Milestone badge unlock toasts
  const [newBadges, setNewBadges] = useState([]);
  const [celebrationBadge, setCelebrationBadge] = useState(null);
  const [isCountdown, setIsCountdown] = useState(false);
  const [countdownTime, setCountdownTime] = useState(5);
  // Floating mini-timer: mirror the current phase while the workout timer runs.
  const floatPhaseRef = useRef({ key: '', total: 1 });
  const floatEx = workout?.exercises?.[currentExercise];
  const floatResting = isRest || isSetRest || isTransition;
  const floatKey = `${isRest}-${isSetRest}-${isTransition}-${currentExercise}-${currentSet}`;
  if (floatPhaseRef.current.key !== floatKey || timeLeft > floatPhaseRef.current.total) {
    floatPhaseRef.current = { key: floatKey, total: Math.max(1, timeLeft) };
  }
  // Float from the Start tap (during the get-ready countdown) so the browser allows opening it.
  useFloatingTimer((isRunning || isCountdown || totalElapsed > 0) && !workoutComplete, {
    mode: 'countdown',
    label: isCountdown ? 'Get ready' : floatResting ? `Rest · next: ${floatEx?.name || ''}` : (floatEx?.name || 'Workout'),
    remainingMs: (isCountdown ? countdownTime : timeLeft) * 1000,
    totalMs: isCountdown ? 5000 : floatPhaseRef.current.total * 1000,
    paused: !isRunning && !isCountdown,
    alert: floatResting && !isCountdown,
    onPause: () => pauseTimer(), onResume: () => resumeTimer(),
    onSkip: floatResting ? () => nextExercise() : undefined,
    onAddTime: floatResting ? () => setTimeLeft((s) => s + 15) : undefined,
  });
  const [buddyInvite, setBuddyInvite] = useState(null);
  const [buddies, setBuddies] = useState([]);
  const [showBuddyPicker, setShowBuddyPicker] = useState(false);
  const [invitingBuddy, setInvitingBuddy] = useState(false);
  const [selectedBuddyIds, setSelectedBuddyIds] = useState([]);
  const [respondingInvite, setRespondingInvite] = useState(false);
  
  const timerRef = useRef(null);
  
  // Refs to avoid stale closures in timer callbacks
  const workoutRef = useRef(workout);
  const currentExerciseRef = useRef(currentExercise);
  const currentSetRef = useRef(currentSet);
  const isRestRef = useRef(isRest);
  const isSetRestRef = useRef(isSetRest);
  const isTransitionRef = useRef(isTransition);
  
  useEffect(() => { workoutRef.current = workout; }, [workout]);
  useEffect(() => { currentExerciseRef.current = currentExercise; }, [currentExercise]);
  useEffect(() => { currentSetRef.current = currentSet; }, [currentSet]);
  useEffect(() => { isRestRef.current = isRest; }, [isRest]);
  useEffect(() => { isSetRestRef.current = isSetRest; }, [isSetRest]);
  useEffect(() => { isTransitionRef.current = isTransition; }, [isTransition]);

  // Expose "actively working out" globally so IncomingCallContext can auto-decline
  // buddy live calls while the user is mid-workout. We treat the user as busy
  // whenever the timer is running OR a live buddy session is active on this page.
  const { setIsWorkoutActive } = useWorkoutStatus();
  useEffect(() => {
    const busy = isRunning || (!!buddyInvite?.id && buddyInvite?.status === 'active');
    setIsWorkoutActive(busy);
    return () => setIsWorkoutActive(false);
  }, [isRunning, buddyInvite?.id, buddyInvite?.status, setIsWorkoutActive]);

  // ---------------------------------------------------------------------------
  // Live workout sync — when a LiveKit session is active and tied to this
  // workout, mirror Set/Exercise/Timer/Rest state with the buddy via the
  // shared data channel. Either side can drive; latest sentAt wins.
  // ---------------------------------------------------------------------------
  const live = useLiveVideo();
  const liveActive = !!live.session && live.connected;
  const liveLastSyncRef = useRef(0);
  const applyingFromRemoteRef = useRef(false);

  // Subscribe to remote workout-state messages
  useEffect(() => {
    if (!liveActive) return undefined;
    const unsub = live.subscribe((data) => {
      if (!data || data.kind !== 'workout-state') return;
      if (typeof data.sentAt !== 'number') return;
      if (data.sentAt <= liveLastSyncRef.current) return; // stale
      liveLastSyncRef.current = data.sentAt;
      applyingFromRemoteRef.current = true;
      try {
        if (typeof data.exIdx === 'number' && data.exIdx !== currentExerciseRef.current) {
          setCurrentExercise(data.exIdx);
        }
        if (typeof data.curSet === 'number' && data.curSet !== currentSetRef.current) {
          setCurrentSet(data.curSet);
        }
        if (typeof data.timeLeft === 'number') setTimeLeft(data.timeLeft);
        if (typeof data.isRest === 'boolean') setIsRest(data.isRest);
        if (typeof data.isSetRest === 'boolean') setIsSetRest(data.isSetRest);
        if (typeof data.isTransition === 'boolean') setIsTransition(data.isTransition);
        if (typeof data.isRunning === 'boolean') {
          // Drive the local interval to match the buddy. Without this, the
          // receiver only flips the boolean but their setInterval never starts,
          // so the timer doesn't actually count down on their screen.
          if (data.isRunning && !timerRef.current) {
            // Match remote's running state — start a tick-down interval here too.
            // We skip the 5-second countdown since the host already did it.
            setIsRunning(true);
            timerRef.current = setInterval(() => {
              setTimeLeft((prev) => {
                if (prev === 4 || prev === 3 || prev === 2) playCountdownBeep();
                if (prev <= 1) { playCompleteSound(); return 0; }
                return prev - 1;
              });
              setTotalElapsed((p) => p + 1);
            }, 1000);
          } else if (!data.isRunning && timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            setIsRunning(false);
          } else {
            setIsRunning(data.isRunning);
          }
        }
      } finally {
        // Drop the guard on the next tick so our own broadcast effect doesn't echo back
        setTimeout(() => { applyingFromRemoteRef.current = false; }, 50);
      }
    });
    return unsub;
    // playCountdownBeep / playCompleteSound are stable at runtime
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveActive, live]);

  // Broadcast local workout-state changes to the buddy (skip the broadcast we
  // just applied from a remote message)
  useEffect(() => {
    if (!liveActive) return;
    if (applyingFromRemoteRef.current) return;
    const payload = {
      kind: 'workout-state',
      exIdx: currentExercise,
      curSet: currentSet,
      timeLeft,
      isRest,
      isSetRest,
      isTransition,
      isRunning,
    };
    liveLastSyncRef.current = Date.now();
    live.publish(payload);
    // We deliberately do NOT include `timeLeft` in the dep array — it changes
    // every tick and broadcasting 60×/min is wasteful. Only major transitions
    // (set, exercise, rest mode, run/pause) trigger a broadcast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveActive, currentExercise, currentSet, isRest, isSetRest, isTransition, isRunning]);

  useEffect(() => {
    const fetchWorkout = async () => {
      try {
        const [workoutRes, favsRes] = await Promise.all([
          api().get(`/workouts/${id}`),
          api().get('/favorites/ids')
        ]);
        setWorkout(workoutRes.data);
        setIsFavorite(favsRes.data.includes(id));
        if (workoutRes.data.exercises?.length > 0) {
          setTimeLeft(workoutRes.data.exercises[0].duration_seconds);
        }
        // Fetch active buddy session for this workout
        try {
          const inviteRes = await api().get(`/buddies/workout-invite/active/${id}`);
          const session = inviteRes.data.active_session;
          setBuddyInvite(session);
          // If both have already accepted by the time we load this page,
          // auto-launch (guarded by sessionStorage so a previously-launched
          // session won't re-trigger if the user comes back).
          if (session && (session.participants || []).filter(p => p.status === 'accepted').length >= 2) {
            maybeAutoLaunchLive(session);
          }
        } catch (e) { console.debug('No active buddy session:', e.message); }
      } catch (err) {
        // 402 = Payment Required → workout is premium-only; redirect user
        // to the subscription page (instead of bouncing to /workouts).
        if (err.response?.status === 402) {
          toast.info(err.response?.data?.detail || 'This workout is for premium members. Upgrade to unlock.');
          navigate('/subscription');
        } else {
          toast.error('Failed to load workout');
          navigate('/workouts');
        }
      }
      setLoading(false);
    };
    fetchWorkout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll buddy invite every 8s while it's active and not everyone is completed,
  // so the host (Jallesa) sees Dainer's status flip from Pending → Accepted live.
  useEffect(() => {
    if (!buddyInvite?.id) return undefined;
    const allDone = (buddyInvite.participants || []).every(p => p.status === 'declined' || p.completed);
    if (allDone) return undefined;
    const t = setInterval(async () => {
      try {
        const res = await api().get(`/buddies/workout-invite/active/${id}`);
        const next = res.data.active_session;
        if (!next) return;
        // Detect newly-accepted participants since the last snapshot and fire
        // an instant toast for each (host-side delight + status flip in real time).
        const prevById = new Map((buddyInvite.participants || []).map(p => [p.user_id, p]));
        const newlyAccepted = (next.participants || []).filter(p => {
          if (p.user_id === user?.id) return false; // don't toast yourself
          const prev = prevById.get(p.user_id);
          return prev && prev.status !== 'accepted' && p.status === 'accepted';
        });
        for (const p of newlyAccepted) {
          toast.success(`${p.name || 'Your buddy'} joined the workout!`, { icon: '🎉' });
        }
        // Detect newly-completed
        const newlyCompleted = (next.participants || []).filter(p => {
          if (p.user_id === user?.id) return false;
          const prev = prevById.get(p.user_id);
          return prev && !prev.completed && p.completed;
        });
        for (const p of newlyCompleted) {
          toast.success(`${p.name || 'Your buddy'} crushed the workout! 💪`);
        }
        setBuddyInvite(next);

        // Auto-launch the live session as soon as we transition to "everyone accepted".
        // Both buddies just opted in — skip the extra Go Live click. Only triggers
        // ONCE per session (sessionStorage flag) so cancelling the preflight and
        // returning here doesn't keep auto-relaunching.
        const prevAccepted = (buddyInvite.participants || []).filter(p => p.status === 'accepted').length;
        const nextAccepted = (next.participants || []).filter(p => p.status === 'accepted').length;
        if (prevAccepted < 2 && nextAccepted >= 2) {
          maybeAutoLaunchLive(next);
        }
      } catch (e) {
        // Polling retries every 8s — quietly log so the loop keeps running
        // even if a single poll fails (network blip, brief auth reshuffle).
        console.debug('buddyInvite poll failed:', e?.message);
      }
    }, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buddyInvite?.id, buddyInvite?.participants, id]);

  // Auto-launch helper — guarded by a sessionStorage dedup flag to avoid
  // loops if the user bails out of the preflight and navigates back to this
  // page. The stored value is a single literal '1' per session id — NO auth
  // tokens, NO personal data, NO session identifiers beyond the public
  // live-session uuid (which is already in the URL).
  const maybeAutoLaunchLive = (session) => {
    if (!session?.id) return;
    const key = `as-auto-launch-${session.id}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (e) {
      // Private mode or quota — fall through so the launch still happens
      // (worst case we auto-launch once more if the user navigates back).
      console.debug('maybeAutoLaunchLive dedup-flag unavailable:', e?.message);
    }
    toast.success('Both accepted — launching live workout!', { icon: '🎬' });
    setTimeout(() => {
      navigate(`/live-workout/${session.id}`);
    }, 800); // brief delay so the toast registers
  };

  // Resolve the current user's participant entry (if any) inside the buddy invite
  const myParticipant = buddyInvite?.participants?.find(p => p.user_id === user?.id);
  const isInvitee = !!myParticipant && buddyInvite?.created_by !== user?.id;
  const myStatus = myParticipant?.status;

  const handleAcceptInvite = async () => {
    if (!buddyInvite?.id) return;
    setRespondingInvite(true);
    try {
      await api().post(`/buddies/workout-invite/${buddyInvite.id}/accept`);
      toast.success("You're in! Time to crush it!");
      const res = await api().get(`/buddies/workout-invite/active/${id}`);
      if (res.data.active_session) {
        setBuddyInvite(res.data.active_session);
        // If accepting just satisfied the quorum, auto-launch on this side too
        const accepted = (res.data.active_session.participants || []).filter(p => p.status === 'accepted').length;
        if (accepted >= 2) {
          maybeAutoLaunchLive(res.data.active_session);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    }
    setRespondingInvite(false);
  };

  const handleDeclineInvite = async () => {
    if (!buddyInvite?.id) return;
    setRespondingInvite(true);
    try {
      await api().post(`/buddies/workout-invite/${buddyInvite.id}/decline`);
      toast.success('Invite declined');
      setBuddyInvite(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    }
    setRespondingInvite(false);
  };

  const handleCancelInvite = async (participant) => {
    if (!buddyInvite?.id) return;
    if (!window.confirm(`Cancel the invite to ${participant.name}?`)) return;
    try {
      const res = await api().delete(`/buddies/workout-invite/${buddyInvite.id}/invitee/${participant.user_id}`);
      toast.success(`Invite to ${participant.name} cancelled`);
      if (res.data?.session_ended) {
        setBuddyInvite(null);
      } else {
        setBuddyInvite((prev) => prev ? {
          ...prev,
          participants: (prev.participants || []).filter(x => x.user_id !== participant.user_id),
        } : prev);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel');
    }
  };

  // Sound effect for timer completion (longer beep)
  const playCompleteSound = useCallback(() => {
    if (soundEnabled) {
      // Longer completion beep
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = 880; // A5 note
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.5);
    }
  }, [soundEnabled]);

  // Countdown beep for 3, 2, 1 seconds
  const playCountdownBeep = useCallback(() => {
    if (soundEnabled) {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = 600; // Lower pitch beep
      oscillator.type = 'sine';
      gainNode.gain.value = 0.2;
      
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.15);
    }
  }, [soundEnabled]);

  const toggleFavorite = async () => {
    try {
      if (isFavorite) {
        await api().delete(`/favorites/${id}`);
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await api().post(`/favorites/${id}`);
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
    } catch (err) {
      toast.error('Failed to update favorites');
    }
  };

  const countdownRef = useRef(null);

  const startTimer = () => {
    if (!workout?.exercises?.length) return;
    
    // If already running (past countdown), just resume
    if (isRunning) return;
    
    // Guard: clear any lingering intervals before starting fresh (prevents double-ticks)
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Start 5-second countdown first
    setIsCountdown(true);
    setCountdownTime(5);
    
    countdownRef.current = setInterval(() => {
      setCountdownTime(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          setIsCountdown(false);
          // Now start the actual workout timer
          setIsRunning(true);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          timerRef.current = setInterval(() => {
            setTimeLeft(prev2 => {
              if (prev2 === 4 || prev2 === 3 || prev2 === 2) {
                playCountdownBeep();
              }
              if (prev2 <= 1) {
                playCompleteSound();
                return 0;
              }
              return prev2 - 1;
            });
            setTotalElapsed(prev2 => prev2 + 1);
          }, 1000);
          return 0;
        }
        playCountdownBeep();
        return prev - 1;
      });
    }, 1000);
  };

  const resumeTimer = () => {
    if (!workout?.exercises?.length || isRunning) return;
    setIsRunning(true);
    // Guard: clear any lingering interval before starting a new one
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev === 4 || prev === 3 || prev === 2) {
          playCountdownBeep();
        }
        if (prev <= 1) {
          playCompleteSound();
          return 0;
        }
        return prev - 1;
      });
      setTotalElapsed(prev => prev + 1);
    }, 1000);
  };

  const pauseTimer = useCallback(() => {
    setIsRunning(false);
    setIsCountdown(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const logWorkout = useCallback(async () => {
    try {
      const res = await api().post('/logs', {
        workout_id: workout?.id,
        workout_title: workout?.title,
        duration_minutes: Math.max(workout?.duration_minutes || 1, Math.ceil(totalElapsed / 60)),
        calories_burned: workout?.calories_burned || workout?.calories_burn || 0
      });
      toast.success('Workout logged! Great job!');

      // Milestone badges unlocked? Show video celebration if the badge has one;
      // otherwise fall back to the small toast. Toast still shows any additional
      // badges not featured by a video.
      if (Array.isArray(res.data?.new_badges) && res.data.new_badges.length > 0) {
        const videoBadge = res.data.new_badges.find((c) => BADGE_CATALOG[c]?.celebration_video);
        const toastBadges = res.data.new_badges.filter((c) => c !== videoBadge);
        if (videoBadge) setCelebrationBadge(videoBadge);
        if (toastBadges.length) setNewBadges((prev) => [...prev, ...toastBadges]);
      }

      // Weekly goal reached? Show the rising-sun celebration.
      if (res.data?.weekly_goal_reached) {
        setCelebrationData({
          weeklyGoal: res.data.weekly_goal,
          workoutTitle: workout?.title,
        });
        setCelebrationOpen(true);
      }

      // If there's an active buddy session, mark as completed
      if (buddyInvite?.id) {
        try {
          const res2 = await api().post(`/buddies/workout-invite/${buddyInvite.id}/complete`);
          if (res2.data.all_completed) {
            toast.success('Everyone in the group crushed it!');
          } else {
            toast.success('Your buddies have been notified you finished!');
          }
          setBuddyInvite(prev => prev ? { ...prev, my_completed: true } : null);
        } catch (e) { console.debug("silent:", e.message); }
      }
    } catch (err) {
      toast.error('Failed to log workout');
    }
  }, [api, workout, totalElapsed, buddyInvite]);

  const nextExercise = useCallback(() => {
    const w = workoutRef.current;
    if (!w?.exercises) return;
    
    const curExIdx = currentExerciseRef.current;
    const curSet = currentSetRef.current;
    const exercise = w.exercises[curExIdx];
    const totalSets = exercise?.sets || 1;
    const difficulty = w.difficulty?.toLowerCase();
    const defaultRest = difficulty === 'advanced' ? 20 : 30;
    const exerciseRest = (exercise?.rest_seconds > 0) ? exercise.rest_seconds : defaultRest;
    
    if (isTransitionRef.current) {
      // Transition over → start next exercise
      setIsTransition(false);
      setCurrentSet(1);
      setTimeLeft(w.exercises[curExIdx].duration_seconds);
    } else if (isSetRestRef.current) {
      setIsSetRest(false);
      setTimeLeft(exercise.duration_seconds);
    } else if (isRestRef.current) {
      setIsRest(false);
      setCurrentSet(1);
      setTimeLeft(w.exercises[curExIdx].duration_seconds);
    } else if (curSet < totalSets) {
      // More sets remaining → set rest
      setCurrentSet(prev => prev + 1);
      setIsSetRest(true);
      setTimeLeft(exerciseRest);
    } else if (curExIdx < w.exercises.length - 1) {
      // All sets done, more exercises → transition time from current exercise
      const transitionTime = exercise?.transition_seconds || 45;
      setCurrentExercise(prev => prev + 1);
      setIsTransition(true);
      setTimeLeft(transitionTime);
    } else {
      pauseTimer();
      setWorkoutComplete(true);
      logWorkout();
    }
  }, [pauseTimer, logWorkout]);

  useEffect(() => {
    if (timeLeft === 0 && isRunning) {
      nextExercise();
    }
  }, [timeLeft, isRunning, nextExercise]);

  const resetTimer = useCallback(() => {
    pauseTimer();
    setCurrentExercise(0);
    setCurrentSet(1);
    setTotalElapsed(0);
    setIsRest(false);
    setIsSetRest(false);
    setIsTransition(false);
    setIsCountdown(false);
    setCountdownTime(5);
    setWorkoutComplete(false);
    if (workout?.exercises?.length > 0) {
      setTimeLeft(workout.exercises[0].duration_seconds);
    }
  }, [pauseTimer, workout]);

  const fetchBuddies = async () => {
    try {
      const res = await api().get('/buddies/');
      setBuddies(res.data.buddies || []);
      setSelectedBuddyIds([]);
      setShowBuddyPicker(true);
    } catch { toast.error('Failed to load buddies'); }
  };

  const toggleBuddySelection = (buddyId) => {
    setSelectedBuddyIds(prev => 
      prev.includes(buddyId) 
        ? prev.filter(id => id !== buddyId)
        : prev.length < 4 ? [...prev, buddyId] : prev
    );
  };

  const sendBuddyInvite = async () => {
    if (selectedBuddyIds.length === 0) {
      toast.error('Select at least one buddy');
      return;
    }
    setInvitingBuddy(true);
    try {
      await api().post('/buddies/workout-invite', { buddy_ids: selectedBuddyIds, workout_id: id });
      toast.success(`Invite sent to ${selectedBuddyIds.length} budd${selectedBuddyIds.length > 1 ? 'ies' : 'y'}!`);
      setShowBuddyPicker(false);
      const inviteRes = await api().get(`/buddies/workout-invite/active/${id}`);
      setBuddyInvite(inviteRes.data.active_session);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    }
    setInvitingBuddy(false);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, []);

  if (loading || !workout) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const currentEx = workout.exercises?.[currentExercise];
  const progress = workout.exercises?.length > 0
    ? ((currentExercise + (isRest ? 0.5 : 0)) / workout.exercises.length) * 100
    : 0;

  const handlePlayPause = () => {
    if (isCountdown || isRunning) {
      pauseTimer();
    } else if (totalElapsed > 0) {
      resumeTimer();
    } else {
      startTimer();
    }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="workout-detail-page">
      <WorkoutDetailHeader isFavorite={isFavorite} onToggleFavorite={toggleFavorite} />

      <WorkoutCompleteModal
        open={workoutComplete}
        totalElapsed={totalElapsed}
        calories={workout.calories_burned}
        onRestart={resetTimer}
      />

      <main className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 [&>*]:min-w-0">
          <WorkoutVideoPlayer
            workout={workout}
            showVideo={showVideo}
            onPlayVideo={() => setShowVideo(true)}
          />

          {/* Buddy Workout Section */}
          <div className="mb-6">
            <BuddyWorkoutPanel
              buddyInvite={buddyInvite}
              currentUserId={user?.id}
              isInvitee={isInvitee}
              myStatus={myStatus}
              respondingInvite={respondingInvite}
              onInviteBuddy={fetchBuddies}
              onAccept={handleAcceptInvite}
              onDecline={handleDeclineInvite}
              onCancelInvite={handleCancelInvite}
              onGoLive={() => navigate(`/live-workout/${buddyInvite.id}`)}
            />
            <BuddyPickerDialog
              open={showBuddyPicker}
              buddies={buddies}
              selectedIds={selectedBuddyIds}
              inviting={invitingBuddy}
              onToggle={toggleBuddySelection}
              onClose={() => setShowBuddyPicker(false)}
              onSend={sendBuddyInvite}
            />
          </div>

          {/* Timer & Exercises */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <WorkoutTimer
              currentEx={currentEx}
              currentExercise={currentExercise}
              totalExercises={workout.exercises?.length}
              currentSet={currentSet}
              isRest={isRest}
              isSetRest={isSetRest}
              isTransition={isTransition}
              isRunning={isRunning}
              isCountdown={isCountdown}
              countdownTime={countdownTime}
              timeLeft={timeLeft}
              totalElapsed={totalElapsed}
              progress={progress}
              soundEnabled={soundEnabled}
              onToggleSound={() => setSoundEnabled(!soundEnabled)}
              onReset={resetTimer}
              onPlayPause={handlePlayPause}
              onSkip={nextExercise}
              onAddTime={() => setTimeLeft(t => t + 15)}
            />

            {/* Live BLE Heart Rate — connects to any smartwatch / HR strap
                broadcasting the standard HR service. Shows live BPM, HR
                zone, avg/max, and Keytel-estimated calories. */}
            <HeartRateWidget workoutId={workout.id} workoutTitle={workout.title} />

            {/* Strength Tracker — log weight + reps per set for PR + 1RM trends.
                Hidden during rest/transition phases to keep focus tight. */}
            {currentEx?.name && !isRest && !isTransition && currentEx.track_weight !== false && (
              <SetLogger
                key={currentEx.id || currentEx.name}
                exerciseName={currentEx.name}
                workoutId={workout.id}
                workoutTitle={workout.title}
                initialSets={currentEx.sets || 3}
                targetWeightLbs={currentEx.target_weight_lbs ?? null}
                targetReps={currentEx.reps ?? null}
                restSeconds={currentEx.rest_seconds ?? 60}
                mainTimerRunning={isRunning || isCountdown}
              />
            )}

            <ExerciseList
              exercises={workout.exercises}
              currentExercise={currentExercise}
              currentSet={currentSet}
            />
          </motion.div>
        </div>
      </main>

      <WeeklyGoalCelebrationModal
        isOpen={celebrationOpen}
        onClose={() => setCelebrationOpen(false)}
        weeklyGoal={celebrationData?.weeklyGoal}
        workoutTitle={celebrationData?.workoutTitle}
      />

      <BadgeUnlockToast
        badges={newBadges}
        onDismiss={() => setNewBadges([])}
      />

      <BadgeCelebrationModal
        isOpen={!!celebrationBadge}
        badgeCode={celebrationBadge}
        onClose={() => setCelebrationBadge(null)}
      />
    </div>
  );
};
